import { DateTime } from 'luxon';

/**
 * Every synced timestamp written to Dexie is normalized to UTC (`...Z`).
 * Lists sort and page on `updateDate` as a *string* (Dexie index order), but
 * the APIs emit ISO strings in their own zone (America/Toronto, so the
 * offset flips between -04:00 and -05:00 with DST) and a client in another
 * zone would emit yet another offset - mixed offsets don't sort
 * chronologically.
 */
export const toUtcIso = (iso: string): string => DateTime.fromISO(iso, { setZone: true }).toUTC().toISO() ?? iso;

/** The current time, as a UTC ISO string - see {@link toUtcIso}. */
export const nowUtcIso = (): string => DateTime.utc().toISO();

/**
 * How far behind its stored cursor a delta pull asks from. The APIs stamp
 * `update_date` with Postgres' `NOW()` - the *transaction start* time, not
 * its commit time - so a long-running transaction (a reading-plan save
 * writing ~1,100 day rows, say) can commit rows dated *before* rows that a
 * shorter, later-starting transaction already committed and that a pull
 * already advanced the cursor past. The APIs' sync horizon (see
 * {@link nextSyncCursor}) closes that gap outright; this window is the
 * fallback for any API without one, and for cursors stored before it
 * existed. Re-asking for this window picks them up; the cost is that the newest rows come back on every pull until
 * something newer arrives, which is why each pull skips a row it already
 * holds at the same `updateDate` rather than re-`put`ting it.
 */
export const SYNC_CURSOR_OVERLAP_SECONDS = 20;

const EPOCH_MS = 0;

/**
 * The `since` to send for a delta pull whose stored cursor is `cursor` -
 * see {@link SYNC_CURSOR_OVERLAP_SECONDS}. Only the request is widened: the
 * cursor itself still advances from the stored value, so it never moves
 * backwards. Never earlier than the epoch, so a first pull's `since` is
 * unchanged.
 */
export const pullSince = (cursor: string): string => {
  const parsed = DateTime.fromISO(cursor, { setZone: true });
  if (!parsed.isValid) return cursor;
  const widened = parsed.toUTC().minus({ seconds: SYNC_CURSOR_OVERLAP_SECONDS });
  return widened.toMillis() <= EPOCH_MS ? DateTime.fromMillis(EPOCH_MS, { zone: 'utc' }).toISO()! : widened.toISO()!;
};

/**
 * The cursor to store after a delta pull that asked from `stored` and saw
 * rows up to `maxSeen`. With the response's sync horizon (see
 * `SYNC_HORIZON_HEADER`) that's the horizon itself: every row up to it is
 * committed and was in the response, while past it a still-open transaction
 * may yet commit rows dated *before* `maxSeen` - and it's a server-clock
 * cursor even when nothing came back. Never moves backwards from `stored`.
 * Without a horizon (an API that can't send one) it's `maxSeen`, as before,
 * with {@link SYNC_CURSOR_OVERLAP_SECONDS} the only cover.
 */
export const nextSyncCursor = (stored: string, maxSeen: string, horizon: string | undefined): string => {
  if (!horizon) return maxSeen;
  const safe = toUtcIso(horizon);
  const floor = toUtcIso(stored);
  return safe > floor ? safe : floor;
};

/**
 * The user's current local *calendar* date (`yyyy-MM-dd`) - what reading
 * plan days are scheduled against (no time zone; see fs-common's
 * ReadingPlanHelpers)
 */
export const localToday = (): string => DateTime.now().toISODate()!;
